import { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogSurface,
  Text,
  Spinner,
  Skeleton,
  SkeletonItem,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Location20Regular,
  CalendarLtr20Regular,
  Document20Regular,
  Settings20Regular,
  Edit20Regular,
  QuestionCircle20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import spaceRoomPlaceholder from '@/assets/images/space-card-room-placeholder.png';
import { BasicButton } from '@components/common/BasicButton';
import { StatusTag } from '../StatusTag';
import { SectionCard } from '../SectionCard';
import { InfoRow } from '../InfoRow';
import { CaterersPanel } from '@components/common/CaterersPanel';
import { FilmingApprovalPanel } from '@components/common/FilmingApprovalPanel';
import { getRoomLayoutMeta } from '@components/spaces/SpaceCard/SpaceCard.constants';
import { useEventDetails } from '@/hooks/useEventDetails';
import { useGetEventHistoryQuery } from '@/store/api/eventService/eventServiceApi';
import { useAuthenticatedImage } from '@/hooks/useAuthenticatedImage';
import { SpaceImageFallback } from '@components/spaces/SpaceImageFallback';
import { telemetry } from '@/services/telemetry';
import { EVENT_TYPES } from '@/constants';
import { VISIT_PURPOSE_OPTIONS } from '@config/bookingFormConfig';
import { formatUtcDateInTimeZone, formatUtcTimeInTimeZone } from '@/utils/timeZone';
import { formatSeriesOrdinalTitle } from '@/utils/seriesEventTitle';
import type { EventId } from '@/store/models/ids';
import type { OnSiteContact } from '@/store/models/events/entities';
import type { EventState as UIEventState } from '@/types/events.types';
import {
  useEventRequestDetailsStyles,
  EVENT_REQUEST_DETAILS_EXIT_MS,
  EVENT_REQUEST_DETAILS_EXIT_REDUCED_MS,
} from './EventRequestDetails.styles';
import { SelectedSpacesAccordion } from './SelectedSpacesAccordion';
import type { ResolvedSelectedSpace } from './SelectedSpacesAccordion';
import { getRoomSetupCapacity, getRoomSetupCount } from './roomSetupCapacity';

const TELEMETRY_COMPONENT_NAME = 'EventDetails';

/** Action types whose comments represent a cancellation or decline reason. */
const REASON_ACTION_TYPES = new Set([
  'Decline',
  'Cancel',
  'RequestCancellation',
  'ConfirmCancellation',
]);

export interface EventRequestDetailsProps {
  open: boolean;
  eventId: EventId | null;
  onClose: () => void;
  onEditClick?: () => void;
  /**
   * Pre-computed display title (already including any series suffix). When
   * provided, it takes precedence over the title derived from the event's
   * `seriesIndex`/`seriesTotal`. Used by surfaces such as the planner list
   * where the suffixed title is already resolved.
   */
  titleOverride?: string;
}

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatDateRange = (
  startDate: string,
  endDate: string,
  locale: string,
  timeZone?: string,
  timeZoneShort?: string
): string => {
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const startDateStr = formatUtcDateInTimeZone(startDate, timeZone);
  const endDateStr = formatUtcDateInTimeZone(endDate, timeZone);
  const startTime = formatUtcTimeInTimeZone(startDate, locale, timeZone, timeOpts);
  const endTime = formatUtcTimeInTimeZone(endDate, locale, timeZone, timeOpts);
  const tz = timeZoneShort ? ` ${timeZoneShort}` : '';

  if (startDateStr === endDateStr) {
    return `${startDateStr} | ${startTime} to ${endTime}${tz}`;
  }
  return `${startDateStr} to ${endDateStr} | ${startTime} to ${endTime}${tz}`;
};

const yesNo = (val: boolean | undefined, t: (key: string) => string): string =>
  val === undefined ? t('notProvided') : val ? t('yes') : t('no');

export const EventRequestDetails = ({
  open,
  eventId,
  onClose,
  onEditClick,
  titleOverride,
}: EventRequestDetailsProps) => {
  const styles = useEventRequestDetailsStyles();
  const { t, i18n } = useTranslation('eventDetails');
  const {
    event,
    building,
    space,
    availableSpaces = [],
    isLoading,
    isError,
    refetch,
  } = useEventDetails(eventId);

  // Fetch event history to extract cancellation/decline reason when not
  // available directly on the event payload.
  const isCancelledOrDeclined =
    event?.eventState === 'CANCELLED' ||
    event?.eventState === 'DECLINED' ||
    event?.eventState === 'SUBMITTED_FOR_CANCELLATION';
  const { data: historyData } = useGetEventHistoryQuery(
    { eventId: eventId as string, buildingId: event?.buildingId as string, top: 50 },
    { skip: !eventId || !event?.buildingId || !isCancelledOrDeclined }
  );
  const cancellationReason = (() => {
    // Prefer the field on the event if the backend populates it.
    if (event?.cancelledReason) return event.cancelledReason;
    if (event?.denialReason) return event.denialReason;
    // Fall back to the most recent cancel/decline history entry with comments.
    if (!historyData?.items) return undefined;
    const entry = [...historyData.items]
      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
      .find((e) => REASON_ACTION_TYPES.has(e.actionType) && e.comments);
    return entry?.comments ?? undefined;
  })();

  const {
    src: spaceImageSrc,
    isLoading: isSpaceImageLoading,
    isError: isSpaceImageError,
  } = useAuthenticatedImage(space?.imageUrl, spaceRoomPlaceholder);
  const showSpaceFallback = !space?.imageUrl || isSpaceImageError;

  // spec 027 FR-004a: append the multi-day series occurrence suffix (e.g.
  // " (1/2)") to the title so series entries are distinguishable, consistent
  // with the dashboard cards. Sourced from the event's API-provided
  // seriesIndex/seriesTotal fields.
  const displayTitle = useMemo(() => {
    if (titleOverride) {
      return titleOverride;
    }
    const baseTitle = event?.eventTitle ?? t('untitled');
    if (!event?.eventId) {
      return baseTitle;
    }
    return formatSeriesOrdinalTitle(baseTitle, event.seriesIndex, event.seriesTotal);
  }, [titleOverride, event?.eventId, event?.eventTitle, event?.seriesIndex, event?.seriesTotal, t]);

  // spec 104 US5: defer the parent's onClose by the exit animation duration
  // so the surface can play its scale-down + fade-out before the Dialog is
  // unmounted. `isAnimatingOut` toggles the exit-keyframes CSS classes.
  // Reduced-motion users get a shorter delay to match the backdrop-only fade.
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Reset the exiting flag whenever the parent re-opens the dialog so a
    // rapid open-after-close starts cleanly with the entry animation.
    if (open) {
      setIsAnimatingOut(false);
      // Focus the close button after Fluent UI's tabster focus trap has
      // finished its own setup (tabster runs in a microtask/rAF after mount).
      // Using setTimeout(0) ensures we win the race without fighting tabster.
      const id = window.setTimeout(() => {
        closeBtnRef.current?.focus({ preventScroll: true });
      }, 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current !== null) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (open && eventId) {
      telemetry.trackEvent(TELEMETRY_COMPONENT_NAME, EVENT_TYPES.NAVIGATION, {
        action: 'EventDetailsModalOpened',
        eventId: eventId as string,
      });
    }
  }, [open, eventId]);

  const handleClose = useCallback(() => {
    telemetry.trackEvent(TELEMETRY_COMPONENT_NAME, EVENT_TYPES.NAVIGATION, {
      action: 'EventDetailsModalClosed',
      eventId: (eventId as string) ?? '',
    });
    // Guard against repeated close requests (Escape held down, double-click
    // on the backdrop) so we don't schedule overlapping timeouts.
    if (isAnimatingOut) {
      return;
    }
    setIsAnimatingOut(true);
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = prefersReducedMotion
      ? EVENT_REQUEST_DETAILS_EXIT_REDUCED_MS
      : EVENT_REQUEST_DETAILS_EXIT_MS;
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      onClose();
    }, delay);
  }, [onClose, eventId, isAnimatingOut]);

  useEffect(() => {
    if (isError && open) {
      telemetry.trackEvent(TELEMETRY_COMPONENT_NAME, EVENT_TYPES.NAVIGATION, {
        action: 'EventDetailsLoadFailed',
        eventId: (eventId as string) ?? '',
      });
    }
  }, [isError, open, eventId]);

  const setupCodes = event?.spaces?.[0]?.selectedSetupCodes;
  const configurationLabel = setupCodes
    ?.map((code) => {
      const title = getRoomLayoutMeta(code).title;
      const count = getRoomSetupCount(code, space);
      return count != null ? `${title} (${count})` : title;
    })
    .join(', ');
  const visitPurposeCode = event?.visitPurposeCode;
  const visitPurposeLabel = visitPurposeCode
    ? (VISIT_PURPOSE_OPTIONS.find((option) => option.value === visitPurposeCode)?.labelKey ??
      visitPurposeCode)
    : t('notProvided');
  const showBusinessGroup = building?.businessGroupConfig?.required === true;
  const businessGroupLabel = event?.businessGroupCode
    ? (building?.businessGroupConfig?.businessGroups?.find(
        (group) => group.code === event.businessGroupCode
      )?.label ?? event.businessGroupCode)
    : t('notProvided');

  const buildAddress = (): string => {
    const parts: string[] = [];
    if (building?.buildingName) parts.push(building.buildingName);
    if (space?.floor) parts.push(`Floor ${space.floor}`);
    if (space?.spaceName) parts.push(space.spaceName);
    if (building?.buildingAddress) parts.push(building.buildingAddress);
    return parts.length > 0 ? parts.join(', ') : t('notProvided');
  };

  const spaceCapacity = getRoomSetupCapacity(event?.spaces?.[0], space, event?.numberOfAttendees);

  // Multi-space bookings: when the event reserves more than one space, render
  // the selected-space section as an accordion (one item per space). A single
  // space keeps the existing inline layout.
  const multiSpaceSelections = event?.spaces && event.spaces.length > 1 ? event.spaces : null;
  const resolvedSpaces = useMemo<ResolvedSelectedSpace[]>(
    () =>
      (multiSpaceSelections ?? []).map((selection) => ({
        selection,
        details: availableSpaces.find((s) => s.spaceId === selection.spaceId),
      })),
    [multiSpaceSelections, availableSpaces]
  );

  const eventCoordinationEmails = useMemo(
    () => {
      const seen = new Set<string>();
      return (building?.siteContactEmail ?? [])
        .map((email) => email.trim())
        .filter((email) => {
          const normalized = email.toLowerCase();
          if (!isValidEmail(email) || seen.has(normalized)) return false;
          seen.add(normalized);
          return true;
        });
    },
    [building?.siteContactEmail]
  );

  const renderContacts = (contacts?: OnSiteContact[]) => {
    if (!contacts || contacts.length === 0) {
      return <InfoRow label={t('fields.onSiteContacts')} />;
    }
    return (
      <div>
        <Text className={styles.contactsLabel}>{t('fields.onSiteContacts')}</Text>
        <div className={styles.contactsList}>
          {contacts.map((contact, idx) => (
            <div className={styles.contactItem} key={`contact-${idx}`}>
              <Text className={styles.contactName}>{contact.name}</Text>
              {isValidEmail(contact.email) ? (
                <a className={styles.contactEmail} href={`mailto:${contact.email}`}>
                  {contact.email}
                </a>
              ) : (
                <Text className={styles.contactName}>{contact.email}</Text>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open) {
          handleClose();
        }
      }}
    >
      <DialogSurface
        className={mergeClasses(styles.surface, isAnimatingOut && styles.surfaceExiting)}
        aria-labelledby="event-details-dialog-title"
        aria-describedby={undefined}
        backdrop={{
          className: mergeClasses(styles.backdrop, isAnimatingOut && styles.backdropExiting),
        }}
      >
        <div className={styles.root}>
          <div className={styles.header}>
            <div className={styles.headerRow}>
              {!isLoading && (
                <div className={styles.headerLeft}>
                  <Text as="h2" id="event-details-dialog-title" className={styles.title}>
                    {displayTitle}
                  </Text>
                </div>
              )}
              {!isLoading && (
                <div className={styles.headerCenter}>
                  {event?.eventState && <StatusTag state={event.eventState as UIEventState} />}
                </div>
              )}
              <div className={styles.headerRight}>
                <BasicButton
                  ref={closeBtnRef}
                  variant="subtle"
                  size="medium"
                  icon={<Dismiss24Regular />}
                  onClick={handleClose}
                  aria-label={t('close')}
                  className={styles.closeButton}
                />
              </div>
            </div>
            {!isLoading && event?.requestId && (
              <Text className={styles.requestId}>{t('requestId', { id: event.requestId })}</Text>
            )}
          </div>

          <div className={styles.content}>
            {isLoading && (
              <div className={styles.loadingContainer} data-testid="event-details-loading">
                <Spinner size="medium" label={t('loading')} />
              </div>
            )}

            {isError && !isLoading && (
              <div className={styles.errorContainer} data-testid="event-details-error">
                <Text className={styles.errorTitle}>{t('error.title')}</Text>
                <Text className={styles.errorDescription}>{t('error.description')}</Text>
                <BasicButton variant="primary" size="medium" onClick={() => refetch()}>
                  {t('error.retry')}
                </BasicButton>
              </div>
            )}

            {event && !isLoading && !isError && (
              <>
                {/* Selected Space */}
                <SectionCard
                  title={
                    multiSpaceSelections
                      ? `${t('sections.selectedSpaces')} (${multiSpaceSelections.length})`
                      : t('sections.selectedSpace')
                  }
                  icon={<Location20Regular />}
                  testId="section-selected-space"
                >
                  {multiSpaceSelections ? (
                    <SelectedSpacesAccordion
                      spaces={resolvedSpaces}
                      building={building}
                      fallbackCapacity={event.numberOfAttendees}
                      t={t}
                    />
                  ) : (
                    <div className={styles.spaceSection}>
                      {isSpaceImageLoading ? (
                        <Skeleton aria-label="Loading image">
                          <SkeletonItem className={styles.spaceImage} />
                        </Skeleton>
                      ) : showSpaceFallback ? (
                        <SpaceImageFallback className={styles.spaceImage} />
                      ) : (
                        <img
                          className={styles.spaceImage}
                          src={spaceImageSrc}
                          alt={space?.spaceName ?? event.spaceName ?? t('sections.selectedSpace')}
                        />
                      )}
                      <div className={styles.spaceDetails}>
                        <Text className={styles.spaceName}>
                          {space?.spaceName ?? event.spaceName ?? t('notProvided')}
                        </Text>
                        <Text className={styles.spaceDetailLine}>
                          <span className={styles.spaceDetailLabel}>{t('fields.address')}:</span>{' '}
                          {buildAddress()}
                        </Text>
                        <Text className={styles.spaceDetailLine}>
                          <span className={styles.spaceDetailLabel}>{t('fields.capacity')}:</span>{' '}
                          {spaceCapacity ?? t('notProvided')}
                        </Text>
                        <Text className={styles.spaceDetailLine}>
                          <span className={styles.spaceDetailLabel}>
                            {t('fields.configuration')}:
                          </span>{' '}
                          {configurationLabel ?? t('notProvided')}
                        </Text>
                      </div>
                    </div>
                  )}
                </SectionCard>

                {/* When and Where */}
                <SectionCard
                  title={t('sections.whenAndWhere')}
                  icon={<CalendarLtr20Regular />}
                  testId="section-when-where"
                >
                  <div className={styles.fieldGrid}>
                    <InfoRow
                      label={t('fields.stateProvince')}
                      value={building?.stateName ?? building?.stateCode ?? event.building?.gmtZone}
                    />
                    <InfoRow
                      label={t('fields.dateAndTime')}
                      value={formatDateRange(
                        event.eventStartDate,
                        event.eventEndDate,
                        i18n.language,
                        building?.gmtZone,
                        building?.GMTZoneShort
                      )}
                    />
                    <InfoRow
                      label={t('fields.building')}
                      value={building?.buildingName ?? event.buildingName ?? event.buildingId}
                    />
                    <InfoRow
                      label={t('fields.attendees')}
                      value={String(event.numberOfAttendees)}
                    />
                    {cancellationReason && (
                      <InfoRow label={t('fields.cancellationReason')} value={cancellationReason} />
                    )}
                  </div>
                </SectionCard>

                {/* Meeting Details */}
                <SectionCard
                  title={t('sections.meetingDetails')}
                  icon={<Document20Regular />}
                  testId="section-meeting-details"
                >
                  <div className={styles.fieldGrid3Col}>
                    <InfoRow label={t('fields.meetingName')} value={event.eventTitle} />
                    <InfoRow label={t('fields.attendeeType')} value={event.attendeeTypeCode} />
                    <InfoRow label={t('fields.msFteSponsor')} value={event.sponsor?.name} />
                    <InfoRow label={t('fields.visitPurpose')} value={visitPurposeLabel} />
                    {showBusinessGroup && (
                      <InfoRow label={t('fields.businessGroup')} value={businessGroupLabel} />
                    )}
                    {showBusinessGroup && (
                      <InfoRow
                        label={t('fields.businessJustification')}
                        value={event.businessJustification ?? t('notProvided')}
                      />
                    )}
                    <InfoRow
                      label={t('fields.attendees')}
                      value={String(event.numberOfAttendees)}
                    />
                    {event.sponsor?.costCenter && (
                      <InfoRow label={t('fields.ioCcNumber')} value={event.sponsor.costCenter} />
                    )}
                  </div>
                  <div className={styles.divider} />
                  {renderContacts(event.onSiteContacts)}
                  <div className={styles.divider} />
                  <InfoRow
                    label={t('fields.submittedBy')}
                    value={event.submittedByName ?? event.submittedBy ?? event.createdBy}
                  />
                </SectionCard>

                {/* Additional Details */}
                <SectionCard
                  title={t('sections.additionalDetails')}
                  icon={<Settings20Regular />}
                  testId="section-additional-details"
                  action={
                    onEditClick && (
                      <BasicButton
                        variant="outline"
                        icon={<Edit20Regular />}
                        size="small"
                        onClick={onEditClick}
                      >
                        Edit
                      </BasicButton>
                    )
                  }
                >
                  <div className={styles.additionalGrid}>
                    <InfoRow
                      label={t('fields.cateringRequired')}
                      value={yesNo(event.hasCatering, t)}
                    />
                    <InfoRow
                      label={t('fields.eventFilmed')}
                      value={yesNo(event.filmingRequired, t)}
                    />
                    <InfoRow
                      label={t('fields.overnightStorage')}
                      value={yesNo(event.overnightStorageRequired, t)}
                    />
                    <InfoRow
                      label={t('fields.anyShipments')}
                      value={yesNo(event.shipmentsExpected, t)}
                    />
                  </div>

                  {event.hasCatering && building?.caterers && building.caterers.length > 0 && (
                    <CaterersPanel caterers={building.caterers} />
                  )}

                  {event.filmingRequired && (
                    <FilmingApprovalPanel
                      filmingFormUrl={building?.filmingFormUrl}
                      gsocEmail={building?.gsocEmail}
                      regionalSecurityAdvisorEmail={building?.regionalSecurityAdvisorEmail}
                      siteContactEmail={building?.siteContactEmail}
                    />
                  )}

                  {event.additionalInformation && (
                    <>
                      <div className={styles.divider} />
                      <div>
                        <Text className={styles.additionalTextLabel}>
                          {t('fields.additionalDetails')}
                        </Text>
                        <Text className={styles.additionalText}>{event.additionalInformation}</Text>
                      </div>
                    </>
                  )}
                </SectionCard>

                {eventCoordinationEmails.length > 0 && (
                  <SectionCard
                    title={t('sections.needHelp')}
                    icon={<QuestionCircle20Regular />}
                    testId="section-event-coordination"
                  >
                    <div className={styles.eventCoordinationRow}>
                      <Text className={styles.eventCoordinationLabel}>
                        {t('fields.eventCoordination')}
                      </Text>
                      <div className={styles.eventCoordinationLinks}>
                        {eventCoordinationEmails.map((email) => (
                          <a
                            key={email}
                            className={styles.contactEmail}
                            href={`mailto:${email}`}
                            aria-label={t('actions.contactEventCoordination', { email })}
                          >
                            {email}
                          </a>
                        ))}
                      </div>
                    </div>
                  </SectionCard>
                )}
              </>
            )}
          </div>
        </div>
      </DialogSurface>
    </Dialog>
  );
};

EventRequestDetails.displayName = 'EventRequestDetails';
